/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';
import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import {
	ROADMAP_VIEW_SPOTLIGHT_DISMISSED,
	ROADMAP_COLUMN_CONFIG_SPOTLIGHT_DISMISSED,
} from '../../../constants.tsx';
import type { Props, State } from '../../../types.tsx';
import { maybeWaitForPremiumModal } from '../../utils.tsx';

export const loadRoadmapViewSpotlightDismissed =
	(): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		await maybeWaitForPremiumModal(getState);

		// User property is already loaded
		if (typeof getState().roadmapView.spotlightDismissed === 'boolean') {
			return;
		}

		try {
			const spotlightDismissed = await getUserProperty<boolean>(
				accountId,
				ROADMAP_VIEW_SPOTLIGHT_DISMISSED,
			);

			const state = getState();

			setState({
				...state,
				roadmapView: {
					...state.roadmapView,
					spotlightDismissed,
				},
			});
		} catch (error) {
			// Expected error, the property is not set yet
			if (error instanceof FetchError && error.statusCode === 404) {
				const state = getState();

				setState({
					...state,
					roadmapView: {
						...state.roadmapView,
						spotlightDismissed: false,
					},
				});

				return;
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.roadmap-view-spotlight',
				`Failed to get user property with key: ${ROADMAP_VIEW_SPOTLIGHT_DISMISSED}`,
				error instanceof Error ? error : undefined,
			);
		}
	};

export const setRoadmapViewSpotlightDismissed =
	(): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		const state = getState();

		setState({
			...state,
			roadmapView: {
				...state.roadmapView,
				spotlightDismissed: true,
			},
		});

		try {
			await setUserProperties(accountId, ROADMAP_VIEW_SPOTLIGHT_DISMISSED, 'true');
		} catch (error) {
			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.roadmap-view-spotlight',
				`Failed to set user property with key: ${ROADMAP_VIEW_SPOTLIGHT_DISMISSED}`,
				error instanceof Error ? error : undefined,
			);
		}
	};

export const loadRoadmapColumnConfigSpotlightDismissed =
	(): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		// User property is already loaded
		if (typeof getState().roadmapView.columnConfigSpotlightDismissed === 'boolean') {
			return;
		}

		try {
			const columnConfigSpotlightDismissed = await getUserProperty<boolean>(
				accountId,
				ROADMAP_COLUMN_CONFIG_SPOTLIGHT_DISMISSED,
			);

			const state = getState();

			setState({
				...state,
				roadmapView: {
					...state.roadmapView,
					columnConfigSpotlightDismissed,
				},
			});
		} catch (error) {
			// Expected error, the property is not set yet
			if (error instanceof FetchError && error.statusCode === 404) {
				const state = getState();

				setState({
					...state,
					roadmapView: {
						...state.roadmapView,
						columnConfigSpotlightDismissed: false,
					},
				});

				return;
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.all-views-spotlight',
				`Failed to get user property with key: ${ROADMAP_COLUMN_CONFIG_SPOTLIGHT_DISMISSED}`,
				error instanceof Error ? error : undefined,
			);
		}
	};

export const setRoadmapColumnConfigSpotlightDismissed =
	(): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		const state = getState();

		setState({
			...state,
			roadmapView: {
				...state.roadmapView,
				columnConfigSpotlightDismissed: true,
			},
		});

		try {
			await setUserProperties(accountId, ROADMAP_COLUMN_CONFIG_SPOTLIGHT_DISMISSED, 'true');
		} catch (error) {
			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.roadmap-view-column-config-spotlight',
				`Failed to set user property with key: ${ROADMAP_COLUMN_CONFIG_SPOTLIGHT_DISMISSED}`,
				error instanceof Error ? error : undefined,
			);
		}
	};
